"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ThemeToggle } from "@/lib/theme";
import { createClient } from "@/lib/supabase/client";
import { ProfileDropdown } from "@/components/profile-dropdown";
import { BetaPill } from "@/components/beta-banner";
import { Logo } from "@/components/brand/Logo";

import { StatusDot } from "@/components/catalog/status";
import { ALL_EXAMS } from "./landing-data";

/** Flagship = the five complete, exam-identical tests — first-class in the dropdown. */
const FLAGSHIP: { slug: string; name: string }[] = [
  { slug: "toefl", name: "TOEFL iBT" },
  { slug: "ielts", name: "IELTS Academic" },
  { slug: "sat",   name: "SAT" },
  { slug: "act",   name: "ACT" },
  { slug: "gre",   name: "GRE" },
];

/** Category links with live counts, derived from the shared catalog data. */
const CATEGORY_LINKS = ([
  ["academic",     "Admissions"],
  ["english",      "English"],
  ["medical",      "Medical"],
  ["language",     "Languages"],
  ["professional", "Professional"],
  ["licensing",    "Licensing"],
] as const).map(([id, label]) => ({
  id,
  label,
  count: ALL_EXAMS.filter(e => e.category === id).length,
}));

interface LandingNavProps {
  /** Hide the theme toggle (used on landing + browse pages per May 2026 redesign). */
  showThemeToggle?: boolean;
}

export function LandingNav({ showThemeToggle = true }: LandingNavProps = {}) {
  const [user, setUser] = useState<{ email: string; display_name?: string | null } | null>(null);
  const [examsOpen, setExamsOpen] = useState(false);
  const [dropdownAlign, setDropdownAlign] = useState<"left" | "right">("left");
  const [mobileOpen, setMobileOpen] = useState(false);
  const examsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(async ({ data: { user: u } }) => {
      if (!u) return;
      const { data } = await supabase
        .from("users")
        .select("email, display_name")
        .eq("id", u.id)
        .single();
      setUser({ email: data?.email ?? u.email ?? "", display_name: data?.display_name });
    });
  }, []);

  // Close desktop dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (examsRef.current && !examsRef.current.contains(e.target as Node)) setExamsOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [mobileOpen]);

  const signedIn = !!user;

  return (
    <>
      <div className="sticky top-0 z-30 bg-cream-glass border-b border-cream">
        <nav className="px-4 sm:px-6 h-16 flex items-center">
          <div className="max-w-6xl w-full mx-auto flex items-center justify-between">

            {/* Logo (new infinity + arrow mark) */}
            <div className="flex items-center gap-3 min-w-0">
              <Link
                href="/"
                className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-[var(--text-primary)]"
                aria-label="FullPracticeTests home"
              >
                <Logo size={30} />
                <span className="hidden xs:inline">FullPracticeTests</span>
              </Link>
              <BetaPill />
            </div>

            {/* Desktop right side */}
            <div className="hidden sm:flex items-center gap-5">
              {signedIn && showThemeToggle && <ThemeToggle />}

              {/* Desktop exams dropdown */}
              <div className="relative" ref={examsRef}>
                <button
                  onClick={() => {
                    const opening = !examsOpen;
                    if (opening && examsRef.current) {
                      const rect = examsRef.current.getBoundingClientRect();
                      setDropdownAlign(window.innerWidth - rect.left >= 656 ? "left" : "right");
                    }
                    setExamsOpen(v => !v);
                  }}
                  className="flex items-center gap-1 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  Exams
                  <svg className={`w-3.5 h-3.5 transition-transform ${examsOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {examsOpen && (
                  <div className={`absolute ${dropdownAlign === "left" ? "left-0" : "right-0"} top-8 w-[520px] bg-white rounded-xl shadow-lg border border-gray-200 p-4 z-50 grid grid-cols-[1.2fr_1fr] gap-4`}>
                    {/* Left pane: flagship exams with one-click start */}
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 px-2 pb-1.5">
                        Full-length flagship
                      </div>
                      {FLAGSHIP.map(exam => (
                        <Link
                          key={exam.slug}
                          href={`/${exam.slug}`}
                          onClick={() => setExamsOpen(false)}
                          className="group flex items-center justify-between px-2.5 py-2 text-sm text-gray-800 hover:bg-emerald-50/60 rounded-lg transition-colors"
                        >
                          <span className="flex items-center gap-2 font-medium">
                            {exam.name} <StatusDot />
                          </span>
                          <span className="text-[11px] text-gray-400 group-hover:text-emerald-700 font-medium">Start →</span>
                        </Link>
                      ))}
                    </div>

                    {/* Right pane: categories with counts */}
                    <div className="min-w-0 border-l border-gray-100 pl-4">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 pb-1.5">
                        By category
                      </div>
                      {CATEGORY_LINKS.map(c => (
                        <Link
                          key={c.id}
                          href={`/exams?category=${c.id}`}
                          onClick={() => setExamsOpen(false)}
                          className="flex items-center justify-between py-1.5 text-sm text-gray-700 hover:text-[var(--text-primary)] transition-colors"
                        >
                          {c.label}
                          <span className="text-[11px] text-gray-400 tabular-nums">{c.count}</span>
                        </Link>
                      ))}
                      <Link
                        href="/exams"
                        onClick={() => setExamsOpen(false)}
                        className="mt-2 inline-block text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        All {ALL_EXAMS.length} exams →
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {signedIn && user ? (
                <div className="flex items-center gap-3">
                  <Link
                    href="/dashboard"
                    className="text-sm bg-[var(--text-primary)] text-[var(--bg-primary)] px-5 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  >
                    Dashboard →
                  </Link>
                  <ProfileDropdown email={user.email} displayName={user.display_name} />
                </div>
              ) : (
                <>
                  <Link href="/auth" className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">Sign in</Link>
                  <Link
                    href="/auth?mode=signup"
                    className="text-sm bg-[var(--text-primary)] text-[var(--bg-primary)] px-5 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  >
                    Start free →
                  </Link>
                </>
              )}
            </div>

            {/* Mobile: right side (sign-in pill + hamburger) */}
            <div className="flex sm:hidden items-center gap-2">
              {signedIn ? (
                <Link href="/dashboard" className="text-xs bg-[var(--text-primary)] text-[var(--bg-primary)] px-3 py-1.5 rounded-lg font-semibold">
                  Dashboard
                </Link>
              ) : (
                <Link href="/auth" className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] font-medium">
                  Sign in
                </Link>
              )}
              <button
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

          </div>
        </nav>
      </div>

      {/* ── Mobile drawer ── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 sm:hidden flex">
          {/* Backdrop */}
          <button
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          />

          {/* Panel — slides in from right */}
          <div className="relative ml-auto w-80 max-w-[90vw] h-full bg-white shadow-xl overflow-y-auto flex flex-col">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <Link href="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 font-bold text-[var(--text-primary)]">
                <Logo size={26} />
                FullPracticeTests
              </Link>
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Flagship + categories (same order as the desktop dropdown) */}
            <div className="flex-1 px-4 py-4 space-y-5 overflow-y-auto">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 px-2 mb-1.5">
                  Full-length flagship
                </div>
                <div className="space-y-0.5">
                  {FLAGSHIP.map(exam => (
                    <Link
                      key={exam.slug}
                      href={`/${exam.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-emerald-50 rounded-lg transition-colors"
                    >
                      <span className="flex items-center gap-2 font-medium">{exam.name} <StatusDot /></span>
                      <span className="text-[11px] text-gray-400">Start →</span>
                    </Link>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 px-2 mb-1.5">
                  By category
                </div>
                <div className="space-y-0.5">
                  {CATEGORY_LINKS.map(c => (
                    <Link
                      key={c.id}
                      href={`/exams?category=${c.id}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      {c.label}
                      <span className="text-[11px] text-gray-400 tabular-nums">{c.count}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-3">
                <Link
                  href="/exams"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  See all exams →
                </Link>
              </div>
            </div>

            {/* Drawer footer: auth actions */}
            <div className="border-t border-gray-100 px-5 py-4 space-y-2">
              {signedIn && user ? (
                <>
                  <div className="text-xs text-gray-400 mb-2 px-1 truncate">{user.email}</div>
                  <Link
                    href="/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center bg-[var(--text-primary)] text-[var(--bg-primary)] text-sm font-semibold py-2.5 rounded-xl hover:opacity-90 transition-opacity"
                  >
                    Go to Dashboard →
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    href="/auth?mode=signup"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center bg-[var(--text-primary)] text-[var(--bg-primary)] text-sm font-semibold py-2.5 rounded-xl hover:opacity-90 transition-opacity"
                  >
                    Start free →
                  </Link>
                  <Link
                    href="/auth"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center border border-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Sign in
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
